import React from 'react';
import { Check, ExternalLink } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { useInView, usePrefersReducedMotion } from '../motion';
import {
  AppMockupChrome,
  ChipTone,
  MetricCell,
  MetricChip,
  MockCard,
  ProductMockupBody,
  ProductMockupFrame,
  SkeletonBar,
  TrendIcon,
  TrendTone
} from './shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;

  .slug {
    font-size: 13px;
    font-weight: 700;
    color: #0e1c2b;
  }
`;

const RevisionsTable = styled(MockCard)`
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }

  th {
    text-align: left;
    font-size: 9.5px;
    font-weight: 500;
    color: #b0b7c3;
    padding: 10px 12px 6px;
    white-space: nowrap;

    &.num {
      text-align: right;
    }
  }

  td {
    padding: 9px 12px;
    color: #334155;
    white-space: nowrap;
    vertical-align: middle;

    &.num {
      text-align: right;
      font-weight: 600;
      color: #0e1c2b;
    }
  }

  @media (max-width: 560px) {
    .rpm-col {
      display: none;
    }
  }

  /* Keep the revision label and color-coded CTR chips visible on
     narrow screens; the skeleton metric columns are the ones to drop. */
  @media (max-width: 460px) {
    .pv-col,
    .clicks-col {
      display: none;
    }
  }
`;

const AnimatedRow = styled('tr')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  border-top: 1px solid #f4f4f2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const VerifiedCheck = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${COLORS.PRIMARY};
  flex-shrink: 0;

  svg {
    color: #fff;
  }
`;

const RevisionCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;

  .version {
    font-weight: 700;
    color: #0e1c2b;
  }

  .meta {
    font-size: 10.5px;
    color: #64748b;
  }
`;

const DetailCard = styled('div')<{
  visible: boolean;
  delayMs: number;
  reduced: boolean;
}>`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(14, 28, 43, 0.14);
  border: 1px solid #ececea;
  max-width: 300px;
  margin: -8px 0 4px 14px;
  position: relative;
  z-index: 2;
  opacity: ${(p) => (p.visible || p.reduced ? 1 : 0)};
  transform: ${(p) =>
    p.visible || p.reduced ? 'translateY(0)' : 'translateY(8px)'};
  transition: opacity 0.5s ease ${(p) => p.delayMs}ms,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${(p) => p.delayMs}ms;
`;

const DetailHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 11.5px;
  font-weight: 700;
  color: #0e1c2b;
  gap: 8px;
`;

const DetailBody = styled('div')`
  padding: 10px 16px;
`;

const DetailRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  padding: 4px 0;

  .label {
    color: #64748b;
  }

  .value {
    font-weight: 600;
    color: #0e1c2b;
  }
`;

const DetailFooter = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid #f1f5f9;
`;

const FooterLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #0e1c2b;
`;

const DeltaChip = styled('span')`
  font-size: 9.5px;
  font-weight: 700;
  color: #389e0d;
  background: #f0fbe4;
  border-radius: 100px;
  padding: 2px 7px;
`;

const FooterRpm = styled('span')`
  font-size: 13px;
  font-weight: 800;
  color: #0e1c2b;
`;

type RevisionRow = {
  version: string;
  published: string;
  verified: boolean;
  pageviews: { w: number; trend: TrendTone };
  clicks: { w: number; trend: TrendTone };
  ctr: { value: string; tone: ChipTone };
  rpm: { w: number; trend: TrendTone };
};

const REVISION_ROWS: RevisionRow[] = [
  {
    version: 'v4',
    published: 'Published May 19, 2026',
    verified: true,
    pageviews: { w: 38, trend: 'up' },
    clicks: { w: 36, trend: 'up' },
    ctr: { value: '83.2%', tone: 'best' },
    rpm: { w: 34, trend: 'up' }
  },
  {
    version: 'v3',
    published: 'Published Feb 10, 2026',
    verified: true,
    pageviews: { w: 34, trend: 'up' },
    clicks: { w: 32, trend: 'up' },
    ctr: { value: '52.5%', tone: 'good' },
    rpm: { w: 32, trend: 'up' }
  },
  {
    version: 'v2',
    published: 'Published Dec 4, 2025',
    verified: false,
    pageviews: { w: 30, trend: 'partial' },
    clicks: { w: 28, trend: 'partial' },
    ctr: { value: '11.5%', tone: 'warn' },
    rpm: { w: 28, trend: 'partial' }
  }
];

export const RevisionsMockup = () => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <ProductMockupFrame ref={ref} aria-hidden>
      <AppMockupChrome product="Optimize" surface="Revisions" />
      <ProductMockupBody>
        <TitleRow>
          <span className="slug">/best-long-haul-luggage</span>
          <ExternalLink size={12} color={COLORS.PRIMARY} />
        </TitleRow>

        <RevisionsTable>
          <table>
            <thead>
              <tr>
                <th>Revision</th>
                <th className="num pv-col">Pageviews</th>
                <th className="num clicks-col">Clicks</th>
                <th className="num">Page CTR</th>
                <th className="num rpm-col">RPM</th>
              </tr>
            </thead>
            <tbody>
              {REVISION_ROWS.map((row, index) => (
                <AnimatedRow
                  key={row.version}
                  visible={inView}
                  delayMs={150 + index * 110}
                  reduced={reduced}
                >
                  <td>
                    <RevisionCell>
                      <span className="version">{row.version}</span>
                      <span className="meta">· {row.published}</span>
                      {row.verified && (
                        <VerifiedCheck>
                          <Check size={9} />
                        </VerifiedCheck>
                      )}
                    </RevisionCell>
                  </td>
                  <td className="num pv-col">
                    <MetricCell>
                      <SkeletonBar w={row.pageviews.w} h={7} />
                      <TrendIcon tone={row.pageviews.trend} />
                    </MetricCell>
                  </td>
                  <td className="num clicks-col">
                    <MetricCell>
                      <SkeletonBar w={row.clicks.w} h={7} />
                      <TrendIcon tone={row.clicks.trend} />
                    </MetricCell>
                  </td>
                  <td className="num">
                    <MetricChip tone={row.ctr.tone}>{row.ctr.value}</MetricChip>
                  </td>
                  <td className="num rpm-col">
                    <MetricCell>
                      <SkeletonBar w={row.rpm.w} h={7} />
                      <TrendIcon tone={row.rpm.trend} />
                    </MetricCell>
                  </td>
                </AnimatedRow>
              ))}
            </tbody>
          </table>
        </RevisionsTable>

        <DetailCard visible={inView} delayMs={500} reduced={reduced}>
          <DetailHeader>
            <span>v4 · Published May 19, 2026</span>
            <VerifiedCheck>
              <Check size={9} />
            </VerifiedCheck>
          </DetailHeader>
          <DetailBody>
            <DetailRow>
              <span className="label">Total Pageviews</span>
              <span className="value">5,168</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Active Days</span>
              <span className="value">30</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Earnings</span>
              <span className="value">$3,615.90</span>
            </DetailRow>
          </DetailBody>
          <DetailFooter>
            <FooterLeft>
              RPM
              <DeltaChip>+52.2%</DeltaChip>
            </FooterLeft>
            <FooterRpm>$699.70</FooterRpm>
          </DetailFooter>
        </DetailCard>
      </ProductMockupBody>
    </ProductMockupFrame>
  );
};
